import {
  useBusinessHours,
  useUpdateBusinessHours,
} from '@/hooks/use-business-hours/use-business-hours';
import { Flex, Spin } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import styles from './business-hours.module.scss';
import { BusinessHoursMobile } from './business-hours-mobile';
import { BusinessHoursDesktop } from './business-hours-desktop';
import { CompanySelectBh } from '../company-select-bh/company-select-bh';
import { useCallback, useEffect, useState } from 'react';
import { BusinessHoursModal } from '../business-hours-modal/business-hours-modal';
import Icon, { LoadingOutlined } from '@ant-design/icons';
import Settings from '@/assets/icons/settings-01.svg?react';
import { getHoursForDate } from '@/utils/helpers';
import Card from '@/components/card/card';
import { TimezoneInfo } from '../timezone-info/timezone-info';
import { AfterHoursSettings } from '../after-hours-settings/after-hours-settings';
import { Button } from '../button/button';
import { BusinessHoursPermanent } from '../business-hours-permanent/business-hours-permanent';
import { BusinessHoursDayData } from '../business-hours-day/business-hours-day';
import { useSelectedCompany } from '@/hooks/use-selected-company/use-selected-company';
import { useCompaniesList } from '@/hooks/use-company/use-company';
import { useCompanyStore } from '@/store/app.store';
import { useTranslation } from 'react-i18next';

type TemporaryHoursData = {
  periods: { start: string; end: string }[];
  isClosed: boolean;
  isAllDay: boolean;
};

export function BusinessHours() {
  const { t } = useTranslation('businessHours');
  const { selectedCompany, setSelectedCompany } = useSelectedCompany({
    storageKey: 'selectedCompany_company',
  });
  const { companyName, setCompanyName } = useCompanyStore();
  const { companies } = useCompaniesList();

  useEffect(() => {
    if (companyName && companies) {
      const company = companies.find((c) => c.name === companyName);
      if (company) {
        setSelectedCompany((prev) =>
          prev?.id === company.id
            ? prev
            : { id: company.id, name: company.name, time_zone: company.time_zone }
        );
      }
    }
  }, [companyName, companies]);

  const handleCompanyChange = useCallback(
    (company: typeof selectedCompany) => {
      setSelectedCompany(company);
      setCompanyName(company?.name);
    },
    [setSelectedCompany, setCompanyName]
  );

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(null);
  const [currentMonth, setCurrentMonth] = useState<Dayjs>(() => dayjs());
  const [isBusinessHoursPermanentVisible, setIsBusinessHoursPermanentVisible] = useState(false);

  const companyId = selectedCompany?.id?.toString();
  const dateParam = currentMonth.startOf('month').format('YYYY-MM-DD');

  const { businessHoursData, isLoading } = useBusinessHours(companyId, dateParam);
  const {
    updateCompanyBusinessHours,
    isPendingUpdateBusinessHours,
    updateCompanyBusinessHoursAsync,
  } = useUpdateBusinessHours(companyId);

  const { permanent = [], temporary = [] } = businessHoursData || {};

  const handleSaveTemporary = (date: Dayjs, data: TemporaryHoursData) => {
    if (!companyId) return;

    updateCompanyBusinessHours({
      company_id: companyId,
      business_hours_params: [
        {
          date: date.format('YYYY-MM-DD'),
          status: 'temporary',
          is_closed: data.isClosed,
          all_day: data.isAllDay,
          note: '',
          periods: data.periods,
        },
      ],
    });
  };

  const handleSavePermanent = (data: BusinessHoursDayData[]) => {
    if (!companyId) return;

    const business_hours_params = data.map((day) => ({
      date: dayjs().format('YYYY-MM-DD'),
      status: 'permanent' as const,
      is_closed: day.isClosed,
      all_day: day.isAllDay,
      weekday: day.weekday,
      note: '',
      periods: day.periods,
    }));

    updateCompanyBusinessHoursAsync({
      company_id: companyId,
      business_hours_params,
    })
      .then(() => {
        setIsBusinessHoursPermanentVisible(false);
      })
      // The hook owns the error toast; this only stops the rejection going unhandled.
      .catch(() => undefined);
  };

  const handleDateSelect = (date: Dayjs) => {
    setSelectedDate(date);
    setModalOpen(true);
  };

  const getInitialModalData = () => {
    const hoursArray = getHoursForDate(temporary, permanent, selectedDate);
    const firstPeriod = hoursArray[0];

    const extractTime = (isoString: string) => {
      const match = isoString.match(/T(\d{2}:\d{2})/);
      return match ? match[1] : '';
    };

    const periods = hoursArray
      .filter((h) => !h.is_closed && h.start_time && h.end_time)
      .map((h) => ({
        start: extractTime(h.start_time!),
        end: extractTime(h.end_time!),
      }));

    return {
      periods: periods.length > 0 ? periods : undefined,
      isClosed: firstPeriod?.is_closed || false,
      isAllDay: firstPeriod?.all_day || false,
    };
  };

  if (isLoading) {
    return (
      <div className={styles.loadingWrapper}>
        <Spin indicator={<LoadingOutlined spin />} />
      </div>
    );
  }

  const initialModalData = getInitialModalData();

  const handleCloseBusinessHoursPermanentSettings = () => {
    setIsBusinessHoursPermanentVisible(false);
  };

  const handleOpenBusinessHoursPermanentSettings = () => {
    setIsBusinessHoursPermanentVisible(true);
  };

  return (
    <Flex vertical gap={16}>
      <CompanySelectBh value={selectedCompany} onChange={handleCompanyChange} />

      {selectedCompany && (
        <>
          <Card hint={t('hint', { manage: t('manage') })}>
            <div className={styles.cardBody}>
              <TimezoneInfo timezone={selectedCompany.time_zone} />

              <Button buttonVariant="secondary" onClick={handleOpenBusinessHoursPermanentSettings}>
                <Icon component={() => <Settings className={styles.settingsIcon} />} />
                {t('manage')}
              </Button>

              <div className={styles.mobileView}>
                <BusinessHoursMobile
                  businessHoursData={businessHoursData}
                  currentMonth={currentMonth}
                  onMonthChange={setCurrentMonth}
                  onDateSelect={handleDateSelect}
                />
              </div>

              <div className={styles.desktopView}>
                <BusinessHoursDesktop
                  businessHoursData={businessHoursData}
                  currentMonth={currentMonth}
                  onMonthChange={setCurrentMonth}
                  onDateSelect={handleDateSelect}
                />
              </div>
            </div>
          </Card>

          <AfterHoursSettings
            companyName={selectedCompany.name}
            onTimeZoneSaved={(time_zone) =>
              setSelectedCompany((prev) => (prev ? { ...prev, time_zone } : prev))
            }
          />

          <BusinessHoursModal
            open={modalOpen}
            onClose={() => setModalOpen(false)}
            selectedDate={selectedDate}
            initialPeriods={initialModalData.periods}
            initialIsClosed={initialModalData.isClosed}
            initialIsAllDay={initialModalData.isAllDay}
            onSave={(data) => selectedDate && handleSaveTemporary(selectedDate, data)}
            isLoadingUpdateBusinessHours={isPendingUpdateBusinessHours}
          />
          <BusinessHoursPermanent
            isSaveLoading={isPendingUpdateBusinessHours}
            isVisible={isBusinessHoursPermanentVisible}
            onClose={handleCloseBusinessHoursPermanentSettings}
            onSave={handleSavePermanent}
            permanentHours={permanent}
          />
        </>
      )}
    </Flex>
  );
}
